import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, UserPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const empty = {
  email: "",
  password: "",
  name: "",
  role: "client",
  rut: "",
  razon_social: "",
  id_cliente: "",
  default_responsable: "",
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("Error al cargar usuarios");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (u) => {
    setForm({
      email: u.email,
      password: "",
      name: u.name || "",
      role: u.role,
      rut: u.rut || "",
      razon_social: u.razon_social || "",
      id_cliente: u.id_cliente || "",
      default_responsable: u.default_responsable || "",
    });
    setEditingId(u.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editingId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.email; // don't allow email change here
        await api.patch(`/admin/users/${editingId}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/admin/users", form);
        toast.success("Usuario creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar este usuario?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Administración"
          title="Usuarios"
          description="Gestión de clientes y administradores del sistema."
          actions={
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button
                  onClick={openCreate}
                  data-testid="users-new-btn"
                  className="flex items-center gap-2 px-5 py-3 bg-[#D95A40] hover:bg-[#c94d33] text-white font-bold uppercase tracking-[0.15em] text-xs transition-colors"
                >
                  <UserPlus className="w-4 h-4" />
                  Nuevo usuario
                </button>
              </DialogTrigger>
              <DialogContent className="bg-white sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-display text-xl tracking-tighter">
                    {editingId ? "Editar usuario" : "Nuevo usuario"}
                  </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-2">
                  <Field label="Nombre completo">
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="user-form-name"
                      className="input"
                    />
                  </Field>
                  <Field label="Rol">
                    <Select
                      value={form.role}
                      onValueChange={(v) => setForm({ ...form, role: v })}
                    >
                      <SelectTrigger data-testid="user-form-role" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="client">Cliente</SelectItem>
                        <SelectItem value="admin">Administrador</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Email" className="col-span-2">
                    <input
                      type="email"
                      value={form.email}
                      disabled={!!editingId}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="user-form-email"
                      className="input"
                    />
                  </Field>
                  <Field
                    label={editingId ? "Nueva contraseña (dejar vacío = no cambiar)" : "Contraseña"}
                    className="col-span-2"
                  >
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      data-testid="user-form-password"
                      className="input"
                    />
                  </Field>
                  <Field label="RUT">
                    <input
                      value={form.rut}
                      onChange={(e) => setForm({ ...form, rut: e.target.value })}
                      data-testid="user-form-rut"
                      className="input"
                    />
                  </Field>
                  <Field label="ID_Cliente (SAP)">
                    <input
                      value={form.id_cliente}
                      onChange={(e) => setForm({ ...form, id_cliente: e.target.value })}
                      data-testid="user-form-idcliente"
                      className="input"
                    />
                  </Field>
                  <Field label="Razón social" className="col-span-2">
                    <input
                      value={form.razon_social}
                      onChange={(e) =>
                        setForm({ ...form, razon_social: e.target.value })
                      }
                      data-testid="user-form-razonsocial"
                      className="input"
                    />
                  </Field>
                  <Field label="Responsable por defecto (SAP)" className="col-span-2">
                    <input
                      value={form.default_responsable}
                      onChange={(e) =>
                        setForm({ ...form, default_responsable: e.target.value })
                      }
                      data-testid="user-form-responsable"
                      className="input"
                    />
                  </Field>
                </div>
                <DialogFooter>
                  <button
                    onClick={() => setOpen(false)}
                    className="px-4 py-2 border border-slate-300 text-sm hover:bg-slate-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={save}
                    data-testid="user-form-save"
                    className="px-5 py-2 bg-[#D95A40] hover:bg-[#c94d33] text-white text-sm font-bold uppercase tracking-wider"
                  >
                    {editingId ? "Guardar" : "Crear"}
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          }
        />

        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0B1B3D] text-white">
              <tr>
                <Th>Nombre</Th>
                <Th>Email</Th>
                <Th>Rol</Th>
                <Th>ID_Cliente</Th>
                <Th>Razón Social</Th>
                <Th className="text-right">Acciones</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  className="hover:bg-slate-50"
                >
                  <td className="px-4 py-3 font-semibold text-[#0B1B3D]">
                    {u.name}
                  </td>
                  <td className="px-4 py-3 text-slate-700 font-mono text-xs">
                    {u.email}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${
                        u.role === "admin"
                          ? "bg-[#D95A40] text-white"
                          : "bg-slate-200 text-slate-800"
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{u.id_cliente || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {u.razon_social || "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => openEdit(u)}
                        data-testid={`user-edit-${u.id}`}
                        className="p-2 hover:bg-slate-100 text-slate-600"
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(u.id)}
                        data-testid={`user-delete-${u.id}`}
                        className="p-2 hover:bg-red-50 text-slate-500 hover:text-red-600"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">
                    No hay usuarios
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <style>{`.input{width:100%;padding:0.5rem 0.75rem;border:1px solid #cbd5e1;font-size:0.875rem;background:white}.input:focus{outline:none;border-color:#D95A40;box-shadow:0 0 0 3px rgba(217,90,64,0.2)}.input:disabled{background:#f1f5f9;color:#64748b}`}</style>
    </AppShell>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.15em] ${className}`}
    >
      {children}
    </th>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
